import { type FC, useEffect, useMemo, useRef, useState } from 'react'
import AgoraRTC, { AgoraRTCProvider } from 'agora-rtc-react'

import { AgoraVideoContainer } from './components/AgoraVideoContainer'
import { useStt } from './hooks/useStt'
import { type VideoAtendimentoAgoraProps } from './types'

const extractTextFromProtobuf = (payload: Uint8Array): { text: string; speakerUid: number } => {
  let text = ''
  let speakerUid = 0
  try {
    let i = 0
    while (i < payload.length) {
      const tag = payload[i++]
      const wireType = tag & 7
      const fieldNum = tag >> 3

      if (wireType === 0) {
        let val = 0
        let shift = 0
        while (i < payload.length) {
          const byte = payload[i++]
          val |= (byte & 127) << shift
          if ((byte & 128) === 0) break
          shift += 7
        }
        if (fieldNum === 4) {
          speakerUid = val
        }
      } else if (wireType === 1) {
        i += 8
      } else if (wireType === 5) {
        i += 4
      } else if (wireType === 2) {
        let len = 0
        let shift = 0
        while (i < payload.length && payload[i] >= 128) {
          len |= (payload[i] & 127) << shift
          shift += 7
          i++
        }
        len |= payload[i] << shift
        i++

        if (fieldNum === 10) {
          let end = i + len
          let wordI = i
          while (wordI < end) {
            const wordTag = payload[wordI++]
            const wordWireType = wordTag & 7
            const wordFieldNum = wordTag >> 3
            if (wordWireType === 2) {
              let wLen = 0
              let wShift = 0
              while (wordI < end && payload[wordI] >= 128) {
                wLen |= (payload[wordI] & 127) << wShift
                wShift += 7
                wordI++
              }
              wLen |= payload[wordI] << wShift
              wordI++

              if (wordFieldNum === 1 && wordI + wLen <= payload.length) {
                const wordBytes = payload.slice(wordI, wordI + wLen)
                text += new TextDecoder().decode(wordBytes) + ' '
              }
              wordI += wLen
            } else if (wordWireType === 0) {
              while (wordI < end && payload[wordI] >= 128) wordI++
              if (wordI < end) wordI++
            } else if (wordWireType === 1) {
              wordI += 8
            } else if (wordWireType === 5) {
              wordI += 4
            } else {
              break
            }
          }
        }
        i += len
      } else {
        break
      }
    }
  } catch (e) {
    //
  }

  if (!text) {
    try {
      const raw = new TextDecoder('utf-8', { fatal: false }).decode(payload)
      const matches = raw.match(/[A-Za-zÀ-ÖØ-öø-ÿ0-9.,!?]{2,}/g)
      if (matches) {
        text = matches.join(' ')
      }
    } catch (e2) {}
  }

  return { text: text.trim(), speakerUid }
}

export const VideoAtendimentoAgora: FC<VideoAtendimentoAgoraProps> = ({
  idAtendimento,
  idPessoa,
  isPaciente,
  pacienteNome,
  handleNewUser,
  chatOpen,
  setChatOpen,
  unreadCount,
}) => {
  const client = useMemo(() => {
    return AgoraRTC.createClient({
      mode: 'rtc',
      codec: 'vp8',
    })
  }, [])

  const { isSttEnabled, sttPendente, toggleStt } = useStt(idAtendimento, !isPaciente)
  const [captionText, setCaptionText] = useState('')
  const captionClearTimerRef = useRef<any>(null)

  useEffect(() => {
    if (!isSttEnabled) {
      setCaptionText('')
    }
  }, [isSttEnabled])

  useEffect(() => {
    const handleStreamMessage = (uid: number, payload: Uint8Array) => {
      const { text, speakerUid } = extractTextFromProtobuf(payload)
      if (text && speakerUid !== Number(idPessoa)) {
        setCaptionText(text)

        if (captionClearTimerRef.current) {
          clearTimeout(captionClearTimerRef.current)
        }
        captionClearTimerRef.current = setTimeout(() => {
          setCaptionText('')
        }, 5000)
      }
    }

    client.on('stream-message', handleStreamMessage)
    return () => {
      client.off('stream-message', handleStreamMessage)
      if (captionClearTimerRef.current) {
        clearTimeout(captionClearTimerRef.current)
      }
    }
  }, [client, idPessoa])

  return (
    <AgoraRTCProvider client={client}>
      <div className='position-relative w-100 h-100'>
        <AgoraVideoContainer
          idAtendimento={idAtendimento}
          idPessoa={idPessoa}
          isPaciente={isPaciente}
          pacienteNome={pacienteNome}
          handleNewUser={handleNewUser}
          chatOpen={chatOpen}
          setChatOpen={setChatOpen}
          unreadCount={unreadCount}
          isSttEnabled={isSttEnabled}
          sttPendente={sttPendente}
          onToggleStt={toggleStt}
          captionText={captionText}
        />
      </div>
    </AgoraRTCProvider>
  )
}

export default VideoAtendimentoAgora
